import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { IconTrash } from '@tabler/icons-react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import {
  deleteAvailability,
  getProfessionalAvailability,
} from '../../services/AvailabilityService';
import { getBookings } from '../../services/BookingService';
import { BookingResponseModel } from '../../types/BookingModels';
import { ProfessionalAvailability } from '../../types/AvailabilityModels';
import { useApp } from '../../context/AppContext';
import { CalendarGrid } from '../CalendarGrid';
import { AppToast } from '../AppToast';
import { primaryColor } from '../../constants/theme';

interface TopicAvailabilityManagementProps {
  refreshKey?: number;
}

interface DeleteTarget {
  availabilityId: number;
  timeLabel: string;
}

export const TopicAvailabilityManagement = ({
  refreshKey,
}: TopicAvailabilityManagementProps) => {
  const navigate = useNavigate();
  const { token, profile } = useApp();

  const [displayedMonth, setDisplayedMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [availability, setAvailability] = useState<ProfessionalAvailability | null>(null);
  const [bookings, setBookings] = useState<BookingResponseModel[]>([]);

  const [pendingDelete, setPendingDelete] = useState<DeleteTarget | null>(null);
  const [matchedIds, setMatchedIds] = useState<number[]>([]);
  const [showChoiceDialog, setShowChoiceDialog] = useState(false);
  const [showSingleConfirm, setShowSingleConfirm] = useState(false);

  const [toast, setToast] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);

  const loadData = async () => {
    if (!token || !profile?.id) {
      return;
    }

    try {
      const [availabilityData, bookingsData] = await Promise.all([
        getProfessionalAvailability(profile.id, token),
        getBookings(token),
      ]);

      setAvailability(availabilityData);
      setBookings(
        (bookingsData?.bookings ?? []).filter(
          (booking) => booking.professionalId === profile.id
        )
      );
    } catch (error) {
      console.error('Failed to load management data:', error);
      setToast({ message: 'Unable to load availability data.', variant: 'error' });
    }
  };

  useEffect(() => {
    loadData();
  }, [profile?.id, refreshKey, token]);

  const dotIndicators = useMemo(() => {
    const dots: Record<string, ('green' | 'orange')[]> = {};

    availability?.availableDates.forEach((date) => {
      date.availableTimes.forEach((time) => {
        const iso = format(new Date(time.startIso), 'yyyy-MM-dd');
        dots[iso] = dots[iso] ?? [];
        if (!dots[iso].includes('green')) {
          dots[iso].push('green');
        }
      });
    });

    bookings.forEach((booking) => {
      if (booking.isCancelled) {
        return;
      }
      const iso = format(new Date(booking.startIso), 'yyyy-MM-dd');
      dots[iso] = dots[iso] ?? [];
      if (!dots[iso].includes('orange')) {
        dots[iso].push('orange');
      }
    });

    return dots;
  }, [availability?.availableDates, bookings]);

  const selectedAvailability = useMemo(() => {
    return (
      availability?.availableDates
        .flatMap((date) => date.availableTimes)
        .filter(
          (time) => format(new Date(time.startIso), 'yyyy-MM-dd') === selectedDate
        ) ?? []
    );
  }, [availability?.availableDates, selectedDate]);

  const selectedBookings = useMemo(() => {
    return bookings.filter(
      (booking) => format(new Date(booking.startIso), 'yyyy-MM-dd') === selectedDate
    );
  }, [bookings, selectedDate]);

  const startDelete = (availabilityId: number, timeLabel: string) => {
    const ids =
      availability?.availableDates
        .flatMap((date) => date.availableTimes)
        .filter(
          (time) =>
            `${format(new Date(time.startIso), 'HH:mm')} - ${format(new Date(time.endIso), 'HH:mm')}` ===
            timeLabel
        )
        .map((time) => time.id)
        .filter((id): id is number => typeof id === 'number') ?? [];

    setPendingDelete({ availabilityId, timeLabel });
    setMatchedIds(ids);

    if (ids.length > 1) {
      setShowChoiceDialog(true);
    } else {
      setShowSingleConfirm(true);
    }
  };

  const deleteSingle = async () => {
    if (!pendingDelete || !token) {
      return;
    }

    try {
      await deleteAvailability(pendingDelete.availabilityId, token);
      setToast({ message: 'Availability deleted.', variant: 'success' });
      await loadData();
    } catch (error) {
      console.error('Delete failed:', error);
      setToast({ message: 'Failed to delete availability.', variant: 'error' });
    } finally {
      setPendingDelete(null);
      setShowSingleConfirm(false);
      setShowChoiceDialog(false);
      setMatchedIds([]);
    }
  };

  const deleteAllMatches = async () => {
    if (!token || matchedIds.length === 0) {
      return;
    }

    try {
      await Promise.all(matchedIds.map((id) => deleteAvailability(id, token)));
      setToast({ message: `Deleted ${matchedIds.length} occurrences.`, variant: 'success' });
      await loadData();
    } catch (error) {
      console.error('Delete all failed:', error);
      setToast({ message: 'Failed to delete all occurrences.', variant: 'error' });
    } finally {
      setPendingDelete(null);
      setShowSingleConfirm(false);
      setShowChoiceDialog(false);
      setMatchedIds([]);
    }
  };

  return (
    <Stack gap="md">
      <Paper withBorder p="sm" radius="md">
        <Title order={3} fw="normal" mb="sm">
          Manage Availability
        </Title>

        <CalendarGrid
          displayedMonth={displayedMonth}
          onMonthChange={setDisplayedMonth}
          selectedDates={new Set([selectedDate])}
          onDateClick={(date) => setSelectedDate(format(date, 'yyyy-MM-dd'))}
          dotIndicators={dotIndicators}
          disablePast={false}
        />
      </Paper>

      <Paper withBorder p="sm" radius="md">
        <Stack gap="sm">
          <Title order={4} fw="normal">
            {format(new Date(`${selectedDate}T00:00:00`), 'EEEE, d MMMM yyyy')}
          </Title>

          <Stack gap={8}>
            <Text fw={600} c="orange.7">
              Bookings
            </Text>
            {selectedBookings.map((booking) => (
              <Card
                key={booking.id}
                withBorder
                radius="md"
                style={{ borderColor: 'rgba(245, 159, 0, 0.45)', cursor: 'pointer' }}
                onClick={() => navigate(`/booking/${booking.id}`)}
              >
                <Group justify="space-between" align="center">
                  <Stack gap={2} align="flex-start">
                    <Text fw={600} ta="left">
                      {format(new Date(booking.startIso), 'HH:mm')} - {format(new Date(booking.endIso), 'HH:mm')}
                    </Text>
                    <Text c="dimmed" size="sm" ta="left">
                      {booking.userName}
                    </Text>
                  </Stack>
                  <Badge radius="sm" color="orange" variant="light">
                    Booked
                  </Badge>
                </Group>
              </Card>
            ))}
            {selectedBookings.length === 0 && (
              <Text c="dimmed" size="sm">
                No bookings for this day.
              </Text>
            )}
          </Stack>

          <Stack gap={8}>
            <Text fw={600} c={primaryColor}>
              Available slots
            </Text>
            {selectedAvailability.map((slot) => {
              const timeLabel = `${format(new Date(slot.startIso), 'HH:mm')} - ${format(new Date(slot.endIso), 'HH:mm')}`;
              const topicLabel = slot.topics?.length
                ? slot.topics.map((topic) => topic.title).join(', ')
                : 'Open Session';
              return (
                <Card
                  key={`${slot.id}-${slot.startIso}`}
                  withBorder
                  radius="md"
                  style={{ borderColor: 'rgba(0, 202, 154, 0.4)' }}
                >
                  <Group justify="space-between" align="center">
                    <Stack gap={2} align="flex-start">
                      <Text fw={600} ta="left">{timeLabel}</Text>
                      <Text c="dimmed" size="sm" ta="left">
                        {topicLabel}
                      </Text>
                    </Stack>
                    {slot.id && (
                      <ActionIcon
                        color="red"
                        variant="subtle"
                        onClick={() => startDelete(slot.id as number, timeLabel)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    )}
                  </Group>
                </Card>
              );
            })}
            {selectedAvailability.length === 0 && (
              <Text c="dimmed" size="sm">
                No available slots for this day.
              </Text>
            )}
          </Stack>
        </Stack>
      </Paper>

      <Modal
        opened={showChoiceDialog}
        onClose={() => setShowChoiceDialog(false)}
        title="Delete occurrences"
        centered
      >
        <Stack>
          <Text>
            This slot time appears on other days. Delete only this day, or all {matchedIds.length} occurrences?
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={deleteSingle}>
              Delete this day only
            </Button>
            <Button color="red" onClick={deleteAllMatches}>
              Delete all occurrences ({matchedIds.length})
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={showSingleConfirm}
        onClose={() => setShowSingleConfirm(false)}
        title="Delete availability"
        centered
      >
        <Stack>
          <Text>Delete this availability slot?</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setShowSingleConfirm(false)}>
              Cancel
            </Button>
            <Button color="red" onClick={deleteSingle}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>

      {toast && (
        <AppToast
          message={toast.message}
          variant={toast.variant}
          onClose={() => setToast(null)}
        />
      )}
    </Stack>
  );
};
